import React from 'react'

import {
  Body2,
  Box,
  Display,
  FontWeight,
  Join,
  Link,
  Space,
  TextAlign,
} from '@gousto-internal/citrus-react'

import { useMarketingPricingConfig } from '@library/api-pricing'

import { useIsDiscountGiftEnabled } from 'hooks/useIsDiscountGiftEnabled'
import { useIsBlackFridayPromotion } from 'routes/Home/useIsBlackFridayPromotion/useIsBlackFridayPromotion'

export const discountGiftTerms =
  '**Offer valid for new UK customers only (including Northern Ireland), excluding Jersey, Guernsey, Isle of Man, Scottish Highlands and Islands. Offer ends 28/10/2024. The offer entitles you to 60% off your first box and 20% off all subsequent boxes for two months from the date of sign up. The offer does not include surcharge amounts from premium recipes, Gousto Marketplace items or delivery fees. You will also receive a free 4 x 50g pack of Pots & Co Little Pots 70% Chocolate Ganache with each order for three months from the date of sign up. You will only be entitled to a maximum of 12 packs during the three month period. Cannot cater for dietary requirements. In the event Gousto cannot supply a dessert for any reason, you will need to contact Gousto Customer Care via the methods listed in the help centre to receive a Pots & Co credit voucher. The offer cannot be used in conjunction with any other offers. You will be enrolled into a flexible subscription once the offer is redeemed (skip/pause/cancel at any time for free). See the website for full T&Cs.'

export const blackFridayTerms = `Not including surcharge items and delivery fees. Offer may only be claimed once and applies to the account you are logged into. This offer cannot be used in conjunction with other offers. This offer entitles new customers to 50% off their first box, 40% off their second box and 20% off boxes for the next two months, plus a free 8th box.
The free box will be available on a new customer's 8th order if placed within four months of signing up. The value of the free box discount will be up to £40, excluding surcharge items and delivery fees. If the total cost of the 8th box exceeds £40, the remaining amount will be charged at full price. You must place your first order by 1 December 2025 for this offer to apply, with your first box scheduled for delivery within three weeks of activating the offer.`

export const defaultTerms = `Not including additional charges. Offer may only be claimed once and applies to the account you are logged into. Once discount is claimed, the relevant discount will be applied to your first order and subsequent discount to all following orders delivered within 60 days. This offer cannot be used in conjunction with other offers.`

interface Props {
  children: React.ReactNode
}

export const FooterNotice = ({ children }: Props) => {
  const isDiscountGiftingExperimentEnabled = useIsDiscountGiftEnabled()
  const isBlackFridayPromotion = useIsBlackFridayPromotion()
  const terms = isBlackFridayPromotion ? blackFridayTerms : defaultTerms
  const existingPricing = {
    portionDescription: '5 x 4',
    pricePerPortion: '3.09',
    discountedPricePerPortion: '1.54',
  }

  const newPricing = useMarketingPricingConfig()

  const { portionDescription, pricePerPortion, discountedPricePerPortion } =
    newPricing || existingPricing

  return (
    <Box
      paddingH={[3, 8, 8, 8]}
      paddingV={0}
      paddingBottom={4}
      maxWidth={['100%', '60rem', '60rem', '60rem']}
      style={{ margin: 'auto' }}
    >
      <Join with={<Space size={2} />}>
        {children}

        <Body2>
          ** New customers only.{' '}
          <Box display={Display.Inline} style={{ fontWeight: FontWeight.Bold }}>
            <Link href="https://www.gousto.co.uk/terms-and-conditions">Standard T&Cs apply.</Link>
          </Box>
          {terms}
          <br />
        </Body2>

        <Body2>
          * Based on Gousto having more recipes, and more quick & easy recipes, available on the
          weekly menu than other recipe boxes delivering to the same regions in the UK as Gousto.
          The full details of this comparison can be found here:{' '}
          <Box display={Display.Inline} style={{ fontWeight: FontWeight.Bold }}>
            <Link href="https://cook.gousto.co.uk/choice/">verification of choice</Link>
          </Box>{' '}
          and our{' '}
          <Box display={Display.Inline} style={{ fontWeight: FontWeight.Bold }}>
            <Link href="https://cook.gousto.co.uk/more-quick-and-easy-recipes/">
              biggest ever menu.
            </Link>
          </Box>
        </Body2>

        <Body2>
          <sup>†</sup> Free Next Day Delivery is available for new customers who complete the
          sign-up and checkout process before 11:59am on Friday, Saturday, Monday, and Tuesday. Next
          Delivery availability is limited and so where capacity is reached, customers may see a
          later first delivery date available. Next day delivery is not available across all regions
          (including Northern Ireland) in the UK and is subject to geographical availability.
        </Body2>

        <Body2>
          <sup>‡</sup> £{pricePerPortion} Based on price per portion on a {portionDescription}{' '}
          subscription box. Excl. £3.99 delivery fee & surcharges. £{discountedPricePerPortion}{' '}
          based on new customer price per portion on a {portionDescription} subscription box. Excl.
          surcharges.
        </Body2>

        {isDiscountGiftingExperimentEnabled && (
          <Body2 textAlign={TextAlign.Center}>{discountGiftTerms}</Body2>
        )}
      </Join>
    </Box>
  )
}
